import React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  Banner,
  Button,
  Flex,
  LoadingOrbit,
  NumberField,
  Page,
  PageHeader,
  Progress,
  Select,
  SettingsSection,
  sectionIdFromTitle,
  TableOfContents,
  type TocItem,
  TagBadge,
  Text,
  TextArea,
  TextField,
  type SelectOption,
} from '@chatsdet/ui';
import { FloppyDisk, Play } from '@phosphor-icons/react';
import { MAX_SHARD_TIMEOUT_MS } from '@chatsdet/types';
import { useAuth } from '../../../../contexts/AuthContext';
import { useBreadcrumbs } from '../../../../contexts/BreadcrumbsContext';
import {
  useRunTestSuite,
  useUpdateTestSuite,
  uploadExternalTestBundle,
  type ExternalCapturePolicy,
  type ExternalRetryGranularity,
  type SuiteExternalRunner,
  type TestSuiteDetail,
} from '../../../../features/test-suites';
import {
  useUpdateExternalRunner,
  useUpdateExternalRunnerAdvanced,
  useUpdateExternalRunnerAreas,
} from '../../../../features/test-suites/useExternalRunner';
import { useExternalSuiteConfigData } from '../../../../features/test-suites/useExternalSuiteConfigData';
import {
  classifyEnvMappingRow,
  mappingToRows,
  rowsToMapping,
  type EnvMappingContext,
  type EnvMappingRow,
} from '../../../../features/test-suites/envMappingStatus';
import { parseShardDirectories } from '../../../../features/test-suites/areaPaths';
import { EnvMappingEditor } from './EnvMappingEditor';
import { RepoPicker } from './RepoPicker';
import { ActionParamsEditor, type ActionParamsValues } from './ActionParamsEditor';
import { AreasSection } from './components/AreasSection';
import { useEnvironmentTemplates } from '../../../../hooks/useEnvironmentTemplates';
import { useTemplateActions } from '../../../../features/sandboxes/hooks/useTemplateActions';
import { ScheduleSuiteModal } from '../../../../features/test-suites/ScheduleSuiteModal';
import { canRunExternalSuite, externalSuiteSourceLabel } from './externalSuitePresentation';

const RETRY_GRANULARITY_OPTIONS: SelectOption[] = [
  {
    id: 'test',
    label: 'Retry the failing test',
    description: 'Cheapest, and the only way to tell a flaky test from a broken one',
  },
  {
    id: 'suite',
    label: 'Retry the whole suite',
    description: 'For suites whose tests depend on each other running in order',
  },
];

const CAPTURE_POLICY_OPTIONS: SelectOption[] = [
  {
    id: 'retry',
    label: 'Record diagnostic retries',
    description: 'Record video, console, and network only after a failure',
  },
  {
    id: 'always',
    label: 'Record every attempt',
    description: 'Keep video, console, and network for passing and failing tests',
  },
];

interface RunnerFormState {
  repoId: string | null;
  repoRef: string;
  targetEnvironmentId: string | null;
  credentialId: string | null;
  capturePolicy: ExternalCapturePolicy;
  retryGranularity: ExternalRetryGranularity;
  maxRetries: number;
  envRows: EnvMappingRow[];
  sandboxTemplateId: string | null;
  sandboxActionSlug: string | null;
  sandboxActionParams: ActionParamsValues;
  /** One repository-relative directory (or glob pattern) per line; see `AreasSection`. */
  areaPaths: string;
  /** Raw "Generated prefix" text; empty string means no prefix. */
  areaGeneratedPrefix: string;
}

interface RunnerAdvancedFormState {
  runCmd: string;
  shardCount: number | null;
  shardTimeoutMs: number | null;
}

/**
 * The config surface for a `kind: 'external'` suite.
 *
 * @remarks Rendered instead of the layer canvas, because an external suite has
 * no layers to arrange: its contents are discovered from its repository at run
 * time. This page is intentionally limited to customer-editable configuration.
 *
 * The sections are ordered by what blocks a run: repository, then target, then
 * the `.env` mapping (the suite cannot launch with an empty one — see
 * `runnerLaunchBlocker`), then the tuning that only matters once it runs.
 */
export function ExternalSuiteConfigPage({
  suite,
  runner,
}: {
  suite: TestSuiteDetail;
  runner: SuiteExternalRunner;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isSuperadmin = user?.isSuperadmin === true;

  const updateSuite = useUpdateTestSuite(suite.id);
  const updateRunner = useUpdateExternalRunner(suite.id);
  const updateRunnerAreas = useUpdateExternalRunnerAreas(suite.id);
  const updateRunnerAdvanced = useUpdateExternalRunnerAdvanced(suite.id);
  const runSuite = useRunTestSuite();
  const data = useExternalSuiteConfigData(runner.propertyId);

  const [error, setError] = React.useState<string | null>(null);
  const [name, setName] = React.useState(suite.name);
  const [description, setDescription] = React.useState(suite.description ?? '');
  const [form, setForm] = React.useState<RunnerFormState | null>(null);
  const [advancedForm, setAdvancedForm] = React.useState<RunnerAdvancedFormState | null>(null);
  // Surfaced only right after a template swap discards a stale action/params
  // pair — not restored on load, since a runner that already has no action
  // saved needs no explanation for why it has no action.
  const [sandboxTargetClearedNote, setSandboxTargetClearedNote] = React.useState(false);
  const [bundle, setBundle] = React.useState<File | null>(null);
  const [bundleUploadProgress, setBundleUploadProgress] = React.useState<number | null>(null);
  const bundleInputRef = React.useRef<HTMLInputElement>(null);

  // The org's shared templates hook, not a second one: two hooks of the same
  // name resolving to different query keys is how a cache invalidation ends up
  // refreshing one caller and not the other.
  const sandboxTemplates = useEnvironmentTemplates({
    status: 'verified',
    propertyId: runner.propertyId,
    // Org-wide templates (no property) are legitimate targets — the server's
    // same-property check exempts them — so listing only this property's would
    // hide choices the save would have accepted.
    includeOrgWide: true,
    enabled: !!runner.propertyId,
  });
  const sandboxActions = useTemplateActions(form?.sandboxTemplateId ?? null);

  const repos = data.repos.data?.items ?? [];

  // Hydrate once the suite (or a refetch after a save) lands. Keyed on the
  // runner's `updatedAt`-equivalent fields via the whole object so a background
  // refetch that changed nothing does not stomp an in-progress edit.
  const hydrationKey = React.useMemo(
    () =>
      JSON.stringify([
        runner.id,
        runner.propertyGithubRepoId,
        runner.envMapping,
        runner.sandboxTemplateId,
        runner.sandboxActionSlug,
        runner.sandboxActionParams,
        runner.capturePolicy,
        runner.retryGranularity,
        runner.maxRetries,
        runner.runCmd,
        runner.shardCount,
        runner.shardDirectories,
        runner.shardTimeoutMs,
        runner.areaGeneratedPrefix,
        runner.archivedAt,
      ]),
    [
      runner.id,
      runner.propertyGithubRepoId,
      runner.envMapping,
      runner.sandboxTemplateId,
      runner.sandboxActionSlug,
      runner.sandboxActionParams,
      runner.capturePolicy,
      runner.retryGranularity,
      runner.maxRetries,
      runner.runCmd,
      runner.shardCount,
      runner.shardDirectories,
      runner.shardTimeoutMs,
      runner.areaGeneratedPrefix,
      runner.archivedAt,
    ]
  );
  React.useEffect(() => {
    setForm({
      // The stored link, not a name recovered from the clone URL: the runner
      // persists which `property_github_repos` row it was configured from.
      repoId: runner.propertyGithubRepoId,
      repoRef: runner.repoRef ?? '',
      targetEnvironmentId: runner.targetEnvironmentId,
      credentialId: runner.credentialId,
      capturePolicy: runner.capturePolicy,
      retryGranularity: runner.retryGranularity,
      maxRetries: runner.maxRetries,
      envRows: mappingToRows(runner.envMapping),
      sandboxTemplateId: runner.sandboxTemplateId,
      sandboxActionSlug: runner.sandboxActionSlug,
      sandboxActionParams: runner.sandboxActionParams ?? {},
      areaPaths: (runner.shardDirectories ?? []).join('\n'),
      areaGeneratedPrefix: runner.areaGeneratedPrefix ?? '',
    });
    setAdvancedForm({
      runCmd: runner.runCmd,
      shardCount: runner.shardCount,
      shardTimeoutMs: runner.shardTimeoutMs,
    });
    setName(suite.name);
    setDescription(suite.description ?? '');
    setSandboxTargetClearedNote(false);
    // Deliberately keyed on `hydrationKey` rather than every `runner` field it
    // reads: re-hydrating on any refetch would discard an in-progress edit. The
    // key covers the fields a SAVE changes. `repos` is no longer involved at all
    // — `repoId` now comes straight off the runner, so the list settling can no
    // longer stomp the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hydrationKey,
    suite.name,
    suite.description,
    runner.runCmd,
    runner.shardCount,
    runner.shardDirectories,
    runner.shardTimeoutMs,
    runner.areaGeneratedPrefix,
  ]);

  useBreadcrumbs([
    { key: 'suites', label: 'Suites', href: '/suites' },
    { key: 'builder', label: suite.name },
  ]);

  const environments = data.environments.data?.items ?? [];
  const credentials = data.credentials.data?.items ?? [];
  const personas = data.personas.data ?? [];
  const envDefinitions = data.property.data?.envVarDefinitions ?? [];

  const selectedEnvironment = environments.find((e) => e.id === form?.targetEnvironmentId) ?? null;
  const selectedCredential = credentials.find((c) => c.id === form?.credentialId) ?? null;
  const selectedPersona = personas.find((p) => p.id === selectedCredential?.templateId) ?? null;
  const isPlaywrightSandboxV2 =
    runner.provider === 'playwright' &&
    (runner.targetSandboxV2Environment != null || selectedEnvironment?.sandboxV2Environment != null);

  const mappingContext = React.useMemo<EnvMappingContext>(
    () => ({
      envDefinitions: envDefinitions.map((d) => ({
        name: d.name,
        description: d.description,
        sensitive: d.sensitive,
      })),
      userDefinitions: (selectedPersona?.variableDefinitions ?? []).map((d) => ({
        name: d.name,
        description: d.description,
        sensitive: d.sensitive,
      })),
      // Key presence is the signal: the API omits names with no stored value
      // entirely, and returns sensitive ones masked rather than absent.
      environmentValueKeys: new Set(Object.keys(selectedEnvironment?.envVarValues ?? {})),
      targetEnvironmentId: form?.targetEnvironmentId ?? null,
    }),
    [envDefinitions, selectedPersona, selectedEnvironment, form?.targetEnvironmentId]
  );

  if (!form) {
    return (
      <Page>
        <Flex justify="center" className="py-16">
          <LoadingOrbit size="lg" />
        </Flex>
      </Page>
    );
  }

  const patch = (next: Partial<RunnerFormState>) =>
    setForm((prev) => (prev ? { ...prev, ...next } : prev));

  const mapping = rowsToMapping(form.envRows);
  const mappedCount = Object.keys(mapping).length;
  const unresolvable = form.envRows.filter((row) => {
    const status = classifyEnvMappingRow(row, mappingContext);
    return status.kind === 'no-value' || status.kind === 'unknown-name';
  }).length;

  const saving =
    updateSuite.isPending ||
    updateRunner.isPending ||
    updateRunnerAreas.isPending ||
    updateRunnerAdvanced.isPending ||
    bundleUploadProgress !== null;
  const suiteDirty = name.trim() !== suite.name || description !== (suite.description ?? '');

  const save = async () => {
    setError(null);
    try {
      if (suiteDirty) {
        await updateSuite.mutateAsync({
          name: name.trim() || undefined,
          description: description.trim() ? description : null,
        });
      }
      // The AREAS PATCH validates against the runner command. If this save
      // removes `<spec>` and clears areas together, the ADVANCED PATCH rejects
      // against the still-configured areas, so nothing is written.
      if (isSuperadmin && advancedForm) {
        await updateRunnerAdvanced.mutateAsync(
          isPlaywrightSandboxV2
            ? { shardTimeoutMs: advancedForm.shardTimeoutMs }
            : {
                runCmd: advancedForm.runCmd,
                shardCount: advancedForm.shardCount,
                shardTimeoutMs: advancedForm.shardTimeoutMs,
              }
        );
      }
      await updateRunner.mutateAsync({
        // Only sent when the picker actually resolved a row — the server
        // re-derives the clone URL from it, and sending a stale id would
        // silently re-point the suite at a different repository.
        ...(form.repoId ? { propertyGithubRepoId: form.repoId } : {}),
        ...(runner.sourceKind === 'github' ? { repoRef: form.repoRef.trim() || null } : {}),
        targetEnvironmentId: form.targetEnvironmentId,
        credentialId: form.credentialId,
        envMapping: mapping,
        capturePolicy: form.capturePolicy,
        retryGranularity: form.retryGranularity,
        maxRetries: form.maxRetries,
        sandboxTemplateId: form.sandboxTemplateId,
        sandboxActionSlug: form.sandboxActionSlug,
        // Only meaningful alongside an action — never sent stale/orphaned.
        sandboxActionParams: form.sandboxActionSlug ? form.sandboxActionParams : null,
      });
      const parsedAreaPaths = parseShardDirectories(form.areaPaths) ?? [];
      const savedAreaPaths = runner.shardDirectories ?? [];
      const parsedGeneratedPrefix = form.areaGeneratedPrefix.trim() || null;
      // Only fire this PATCH when the areas (or the prefix) actually changed —
      // the server sorts the paths, so compare sorted so an unrelated save (or
      // a load that never touched this section) never fires it.
      const areasChanged =
        JSON.stringify([...parsedAreaPaths].sort()) !==
          JSON.stringify([...savedAreaPaths].sort()) ||
        parsedGeneratedPrefix !== runner.areaGeneratedPrefix;
      if (areasChanged) {
        await updateRunnerAreas.mutateAsync({
          areaPaths: parsedAreaPaths,
          generatedPrefix: parsedGeneratedPrefix,
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save this suite');
      throw err;
    }
  };

  const chooseBundle = (file: File | undefined) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.tar.gz')) {
      setBundle(null);
      setError('Choose a .tar.gz bundle.');
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      setBundle(null);
      setError('Bundles must be 100 MB or smaller.');
      return;
    }
    setError(null);
    setBundle(
      file.type === 'application/gzip'
        ? file
        : new File([file], file.name, { type: 'application/gzip', lastModified: file.lastModified })
    );
  };

  const uploadBundle = async () => {
    if (!bundle || bundleUploadProgress !== null) return;
    setError(null);
    setBundleUploadProgress(0);
    try {
      await uploadExternalTestBundle(suite.id, bundle, setBundleUploadProgress);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['test-suite', suite.id] }),
        queryClient.invalidateQueries({ queryKey: ['test-suites'] }),
      ]);
      setBundle(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload this bundle');
    } finally {
      setBundleUploadProgress(null);
    }
  };

  const run = async () => {
    try {
      await save();
    } catch {
      return; // save() already surfaced the failure
    }
    setError(null);
    try {
      const result = await runSuite.mutateAsync({ id: suite.id });
      if (result.kind === 'external') {
        navigate({ to: '/runs/external/$runId', params: { runId: result.runId } });
        return;
      }
      navigate({ to: '/runs/test/$executionId', params: { executionId: result.executionId } });
    } catch (err) {
      // `EXTERNAL_TEST_RUN_IN_PROGRESS` lands here with its own message, which
      // names the real problem better than any generic wording could.
      setError(err instanceof Error ? err.message : 'Failed to run this suite');
    }
  };

  const environmentOptions: SelectOption[] = environments.map((env) => ({
    id: env.id,
    label: env.name,
    description: env.url,
  }));
  const credentialOptions: SelectOption[] = credentials.map((cred) => ({
    id: cred.id,
    label: cred.name,
  }));

  const NO_SANDBOX_TEMPLATE = '__none__';
  const sandboxTemplateOptions: SelectOption[] = [
    { id: NO_SANDBOX_TEMPLATE, label: 'No sandbox target' },
    ...(sandboxTemplates.data?.data ?? []).map((t) => ({ id: t.id, label: t.name })),
  ];
  // Only actions the template owner has turned on — a disabled action is not
  // something a customer should be able to schedule against their suite.
  const enabledSandboxActions = (sandboxActions.data ?? []).filter((a) => a.enabled);
  const sandboxActionOptions: SelectOption[] = enabledSandboxActions.map((a) => ({
    id: a.slug,
    label: a.label,
    description: a.description ?? undefined,
  }));
  const selectedSandboxAction =
    enabledSandboxActions.find((a) => a.slug === form.sandboxActionSlug) ?? null;

  // Run launches the persisted runner. Keep the button aligned with that
  // state: edits in this form must be saved before they can make an incomplete
  // uploaded suite look runnable.
  const canRun = canRunExternalSuite({
    sourceKind: runner.sourceKind,
    provider: runner.provider,
    repoId: runner.propertyGithubRepoId,
    bundleStorageKey: runner.bundleStorageKey,
    sandboxTemplateId: runner.sandboxTemplateId,
    sandboxActionSlug: runner.sandboxActionSlug,
    targetSandboxV2Environment: selectedEnvironment?.sandboxV2Environment ?? null,
  });

  // Prefer the linked row's `owner/name` — it is the name the customer picked.
  // Falls back to the clone URL for a runner with no link (the API-key ingest
  // path) or before the repo list has loaded.
  const repoLabel =
    runner.sourceKind === 'github'
      ? (repos.find((r) => r.id === runner.propertyGithubRepoId)?.repoFullName ??
        runner.repoUrl ??
        'GitHub repository')
      : externalSuiteSourceLabel(runner);

  const tocSection = (text: string): TocItem => ({ id: sectionIdFromTitle(text), text, level: 2 });
  const tocHeadings: TocItem[] = [
    tocSection('Suite'),
    tocSection(
      runner.sourceKind === 'github'
        ? 'Repository'
        : runner.sourceKind === 'image'
          ? 'Application image'
          : 'Bundle'
    ),
    tocSection('Target'),
    ...(!isPlaywrightSandboxV2 ? [tocSection('Sandbox target')] : []),
    tocSection('Environment variables'),
    tocSection('Diagnostics'),
    tocSection('Retries'),
    tocSection('Areas'),
    ...(isSuperadmin && advancedForm ? [tocSection('Advanced execution')] : []),
  ];

  return (
    <Page>
      <PageHeader
        title={name || 'Untitled suite'}
        description={
          runner.sourceKind === 'image'
            ? 'Runs Playwright from the application image in the selected Sandbox v2 environment.'
            : `Runs your own ${runner.provider} suite from ${repoLabel}.`
        }
        actions={
          <Flex gap="sm" align="center">
            <TagBadge name="Your own tests" color="teal" size="sm" hideDot />
            <ScheduleSuiteModal suiteId={suite.id} suiteName={suite.name} />
            <Button
              variant="secondary"
              iconLeft={<FloppyDisk size={16} />}
              isDisabled={saving}
              onPress={() => void save()}
            >
              Save
            </Button>
            <Button
              variant="primary"
              iconLeft={<Play size={16} weight="fill" />}
              isDisabled={!canRun || saving || runSuite.isPending}
              onPress={() => void run()}
            >
              Run
            </Button>
          </Flex>
        }
      />

      {error && (
        <Banner variant="error" className="mb-4">
          <Text size="sm">{error}</Text>
        </Banner>
      )}

      {mappedCount === 0 && (
        <Banner variant="info" className="mb-4">
          <Text size="sm">
            {isPlaywrightSandboxV2 ? (
              <>
                Runtime references in <code>cofactor.tests.yaml</code> resolve for each fresh
                sandbox copy.
              </>
            ) : (
              <>
                No variables are mapped. Tests start with an empty <code>.env</code>; map keys below if
                they need credentials or settings.
              </>
            )}
          </Text>
        </Banner>
      )}

      <div className="flex items-start gap-8">
      <Flex direction="column" gap="lg" className="min-w-0 flex-1">
        <SettingsSection
          title="Suite"
          description="How this suite is labelled everywhere it appears."
        >
          <TextField label="Name" value={name} onChange={setName} isRequired />
          <TextArea
            label="Description"
            placeholder="What does this suite cover?"
            value={description}
            onChange={setDescription}
          />
        </SettingsSection>

        {runner.sourceKind === 'github' ? (
          <SettingsSection
            title="Repository"
            description="Where the tests live. Cofactor clones this repository and runs it — your repository is never modified."
          >
            <RepoPicker
              repos={repos}
              isGitHubConnected={data.githubStatus.data?.ok === true}
              propertyId={runner.propertyId}
              selectedRepoId={form.repoId}
              onSelectRepo={(repoId) => patch({ repoId })}
              repoRef={form.repoRef}
              onRepoRefChange={(repoRef) => patch({ repoRef })}
            />
          </SettingsSection>
        ) : runner.sourceKind === 'image' ? (
          <SettingsSection
            title="Application image"
            description="This suite runs Playwright from the application image in its selected Sandbox v2 environment."
          >
            <Text size="sm" className="text-muted-foreground">
              {externalSuiteSourceLabel(runner)}
            </Text>
          </SettingsSection>
        ) : (
          <SettingsSection
            title="Bundle"
            description="This suite runs from its immutable uploaded Playwright bundle."
          >
            <input
              ref={bundleInputRef}
              type="file"
              accept=".tar.gz,application/gzip,application/x-gzip"
              className="sr-only"
              disabled={bundleUploadProgress !== null}
              onChange={(event) => {
                chooseBundle(event.target.files?.[0]);
                event.currentTarget.value = '';
              }}
            />
            <div className="rounded-lg border border-border bg-muted/20 p-3">
              <Flex align="center" justify="between" gap="sm">
                <Flex direction="column" gap="xs" className="min-w-0">
                  <Text size="sm" weight="medium">
                    {bundle
                      ? `${bundle.name} (${Math.ceil(bundle.size / 1024 / 1024)} MB)`
                      : runner.bundleStorageKey
                        ? `Current bundle: ${externalSuiteSourceLabel(runner)}`
                        : 'No bundle uploaded yet'}
                  </Text>
                  <Text size="xs" className="text-muted-foreground text-pretty">
                    {runner.bundleStorageKey
                      ? 'Upload a replacement Playwright .tar.gz bundle when the suite changes.'
                      : 'Choose the Playwright .tar.gz bundle to finish configuring this suite.'}
                  </Text>
                </Flex>
                <Flex gap="sm" className="shrink-0">
                  <Button
                    variant="secondary"
                    isDisabled={bundleUploadProgress !== null}
                    onPress={() => bundleInputRef.current?.click()}
                  >
                    {bundle || runner.bundleStorageKey
                      ? 'Choose replacement bundle'
                      : 'Choose .tar.gz bundle'}
                  </Button>
                  <Button
                    variant="primary"
                    isDisabled={!bundle || bundleUploadProgress !== null}
                    onPress={() => void uploadBundle()}
                  >
                    {bundleUploadProgress !== null ? 'Uploading…' : 'Upload bundle'}
                  </Button>
                </Flex>
              </Flex>
              {bundleUploadProgress !== null && (
                <Progress
                  value={bundleUploadProgress}
                  size="sm"
                  showLabel
                  className="mt-3"
                  aria-label="Bundle upload progress"
                />
              )}
            </div>
          </SettingsSection>
        )}

        <SettingsSection
          title="Target"
          description="Which environment the suite runs against, and which login persona it uses."
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              label="Target environment"
              description="Supplies the app URL and every $var.ENV.* value below."
              placeholder="Choose an environment"
              items={environmentOptions}
              selectedKey={form.targetEnvironmentId}
              onSelectionChange={(key) => patch({ targetEnvironmentId: String(key) })}
              emptyText="This application has no environments yet"
            />
            <Select
              label="Login persona"
              description="Supplies every $var.USER.* value below. Use a least-privilege test account."
              placeholder="Choose a persona"
              items={credentialOptions}
              selectedKey={form.credentialId}
              onSelectionChange={(key) => patch({ credentialId: String(key) })}
              emptyText="This application has no credentials yet"
            />
          </div>
        </SettingsSection>

        {!isPlaywrightSandboxV2 && (
          <SettingsSection
            title="Sandbox target"
            description="Where this suite runs when it runs on a sandbox: Cofactor leases an instance of your template, and the action you pick is what starts the tests on it."
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <Select
                label="Sandbox template"
                description="An instance of this template is leased for each run, and terminated when it finishes."
                items={sandboxTemplateOptions}
                selectedKey={form.sandboxTemplateId ?? NO_SANDBOX_TEMPLATE}
                onSelectionChange={(key) => {
                  const next = String(key);
                  const nextTemplateId = next === NO_SANDBOX_TEMPLATE ? null : next;
                  if (nextTemplateId === form.sandboxTemplateId) return;
                  setSandboxTargetClearedNote(form.sandboxActionSlug !== null);
                  patch({
                    sandboxTemplateId: nextTemplateId,
                    sandboxActionSlug: null,
                    sandboxActionParams: {},
                  });
                }}
                emptyText="This application has no verified sandbox templates yet"
              />
              <Select
                label="Action"
                description="The action that launches your test runner on the leased instance. It receives the image, command and callback details from Cofactor."
                placeholder="Choose an action"
                items={sandboxActionOptions}
                selectedKey={form.sandboxActionSlug}
                isDisabled={!form.sandboxTemplateId}
                onSelectionChange={(key) => {
                  setSandboxTargetClearedNote(false);
                  patch({ sandboxActionSlug: String(key), sandboxActionParams: {} });
                }}
                emptyText={
                  form.sandboxTemplateId
                    ? 'This template has no enabled actions'
                    : 'Choose a sandbox template first'
                }
              />
            </div>
            {sandboxTargetClearedNote && (
              <Text size="xs" className="text-warning text-pretty">
                Changing the template cleared the selected action and its parameters — the previous
                action belonged to a different template's schema.
              </Text>
            )}
            {selectedSandboxAction && (
              <ActionParamsEditor
                schema={selectedSandboxAction.inputSchema}
                values={form.sandboxActionParams}
                onValuesChange={(sandboxActionParams) => patch({ sandboxActionParams })}
              />
            )}
          </SettingsSection>
        )}

        <SettingsSection
          title="Environment variables"
          description="The .env Cofactor writes for your suite — and the complete list of what it is allowed to see."
        >
          <EnvMappingEditor
            rows={form.envRows}
            onRowsChange={(envRows) => patch({ envRows })}
            context={mappingContext}
            propertyId={runner.propertyId}
            environmentName={selectedEnvironment?.name ?? null}
            hasPersona={selectedPersona !== null}
          />
          {unresolvable > 0 && (
            <Text size="xs" className="text-warning">
              {unresolvable} mapped key{unresolvable === 1 ? '' : 's'} will not resolve to a value
              at run time. The suite will still start — those variables simply will not be in the{' '}
              <code>.env</code>.
            </Text>
          )}
        </SettingsSection>

        <SettingsSection
          title="Diagnostics"
          description="Choose which attempts retain browser evidence. Recording every attempt uses more time and storage."
        >
          <Select
            label="Capture policy"
            items={CAPTURE_POLICY_OPTIONS}
            selectedKey={form.capturePolicy}
            onSelectionChange={(key) =>
              patch({ capturePolicy: String(key) as ExternalCapturePolicy })
            }
          />
        </SettingsSection>

        <SettingsSection
          title="Retries"
          description={
            runner.provider === 'playwright'
              ? "What happens when a test fails. Playwright retries a failing test straight away, and the area's time limit includes its retries."
              : 'What happens when a test fails. A retry is a full container run, so this costs real time.'
          }
        >
          <div className="grid gap-4 sm:grid-cols-2">
            {runner.provider !== 'playwright' && (
              <Select
                label="Retry granularity"
                items={RETRY_GRANULARITY_OPTIONS}
                selectedKey={form.retryGranularity}
                onSelectionChange={(key) =>
                  patch({ retryGranularity: String(key) as ExternalRetryGranularity })
                }
              />
            )}
            <NumberField
              label="Maximum retries"
              description="0 means a failure is reported as-is. A test that passes on a retry is reported as flaky, not as a pass."
              minValue={0}
              maxValue={5}
              value={form.maxRetries}
              // Cleared input reads as null; 0 (report the failure as-is) is the
              // only sane reading of "no number here".
              onChange={(maxRetries) => patch({ maxRetries: maxRetries ?? 0 })}
            />
          </div>
        </SettingsSection>

        <AreasSection
          suiteId={suite.id}
          suiteName={suite.name}
          runner={runner}
          value={form.areaPaths}
          onChange={(areaPaths) => patch({ areaPaths })}
          generatedPrefix={form.areaGeneratedPrefix}
          onGeneratedPrefixChange={(areaGeneratedPrefix) => patch({ areaGeneratedPrefix })}
        />

        {isSuperadmin && advancedForm && (
          <SettingsSection
            title="Advanced execution"
            description="Superadmin-only runner settings."
          >
            {!isPlaywrightSandboxV2 && (
              <TextArea
                label="Runner command"
                description="The command executed for each shard. Use <spec> where a directory shard should be substituted."
                value={advancedForm.runCmd}
                onChange={(runCmd) =>
                  setAdvancedForm((current) => (current ? { ...current, runCmd } : current))
                }
                rows={3}
              />
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              {!isPlaywrightSandboxV2 && (
                <NumberField
                  label="Shard count"
                  description="Leave empty for unsharded execution."
                  minValue={1}
                  maxValue={32}
                  value={advancedForm.shardCount}
                  onChange={(shardCount) =>
                    setAdvancedForm((current) => (current ? { ...current, shardCount } : current))
                  }
                />
              )}
              <NumberField
                label="Shard timeout (ms)"
                description="Optional per-shard timeout, bounded by the platform maximum."
                minValue={1}
                maxValue={MAX_SHARD_TIMEOUT_MS}
                value={advancedForm.shardTimeoutMs}
                onChange={(shardTimeoutMs) =>
                  setAdvancedForm((current) =>
                    current ? { ...current, shardTimeoutMs } : current
                  )
                }
              />
            </div>
          </SettingsSection>
        )}
      </Flex>
      <TableOfContents variant="page" headings={tocHeadings} />
      </div>
    </Page>
  );
}
